import React from 'react';
import styles from './MetricsCards.module.css';
import { useCountUp } from '../dashboard';

interface MetricsCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  accentColor: 'gold' | 'pink' | 'blue' | 'green';
  trend?: number;
  unit?: string;
  sparklineData?: number[];
}

const MetricCard: React.FC<MetricsCardProps> = ({ 
  label, 
  value, 
  icon, 
  accentColor,
  trend,
  unit = '',
}) => {
  const numericValue = typeof value === 'number' ? value : Number(value) || 0;
  const animatedValue = useCountUp(numericValue);
  const formattedValue = animatedValue.toLocaleString();

  return (
    <div className={`${styles.metricCard} ${styles[`metric_${accentColor}`]}`}>
      <div className={styles.metricIconBadge} aria-hidden>
        {icon}
      </div>
      <div className={styles.metricContent}>
        <div className={styles.metricLabel}>{label}</div>
        <div className={styles.metricValue}>{formattedValue}{unit ? ` ${unit}` : ''}</div>
      </div>
    </div>
  );
};

interface MetricsCardsProps {
  metrics: {
    totalPolicies: number;
    activeJurisdictions: number;
    totalUsers: number;
    totalAdmins: number;
  };
  isLoading?: boolean;
  trends?: {
    totalPoliciesTrend?: number;
    totalPoliciesHistory?: number[];
    activeJurisdictionsTrend?: number;
  };
}

export const MetricsCards: React.FC<MetricsCardsProps> = ({ 
  metrics,
  isLoading = false,
  trends,
}) => {
  if (isLoading) {
    return (
      <div className={styles.metricsContainer}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={`${styles.metricCard} ${styles.skeleton}`} />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.metricsContainer}>
      <MetricCard
        label="Total Policies Tracked"
        value={metrics.totalPolicies}
        icon={(
          <svg
            viewBox="0 0 24 24"
            aria-hidden
            focusable="false"
            style={{ width: 20, height: 20 }}
          >
            <path
              d="M4 19h16M6 16v-6M12 16V5M18 16v-9"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        )}
        accentColor="gold"
        trend={trends?.totalPoliciesTrend}
        sparklineData={trends?.totalPoliciesHistory}
      />
      <MetricCard
        label="Active Jurisdictions"
        value={metrics.activeJurisdictions}
        icon={(
          <svg
            viewBox="0 0 24 24"
            aria-hidden
            focusable="false"
            style={{ width: 20, height: 20 }}
          >
            <circle
              cx="12"
              cy="12"
              r="9"
              stroke="currentColor"
              strokeWidth="1.8"
              fill="none"
            />
            <path
              d="M3 12h18M12 3c2.5 2.5 3 5.5 3 9s-.5 6.5-3 9"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        )}
        accentColor="pink"
        trend={trends?.activeJurisdictionsTrend}
      />
      <MetricCard
        label="Users"
        value={metrics.totalUsers}
        icon={(
          <svg
            viewBox="0 0 24 24"
            aria-hidden
            focusable="false"
            style={{ width: 20, height: 20 }}
          >
            <circle
              cx="12"
              cy="9"
              r="3.2"
              stroke="currentColor"
              strokeWidth="1.6"
              fill="none"
            />
            <path
              d="M6 19c0-2.5 2.7-4.5 6-4.5s6 2 6 4.5"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        )}
        accentColor="blue"
      />
      <MetricCard
        label="Admins"
        value={metrics.totalAdmins}
        icon={(
          <svg
            viewBox="0 0 24 24"
            aria-hidden
            focusable="false"
            style={{ width: 20, height: 20 }}
          >
            <path
              d="M12 3 6 5v6c0 4.5 3 8.5 6 9 3-0.5 6-4.5 6-9V5Z"
              stroke="currentColor"
              strokeWidth="1.6"
              fill="none"
              strokeLinejoin="round"
            />
            <path
              d="M9.5 11.5 12 9l2.5 2.5"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        )}
        accentColor="green"
      />
    </div>
  );
};
